import React, { useContext, useEffect } from "react";
import {
    ButtonProps,
    ButtonRenderProps,
    ClearFiltersButton,
    ContentSection,
    createListTableColumns,
    depascalize,
    ensure,
    Grid,
    GridColumn,
    GridRow,
    ListTable,
    renderDateTime,
    SearchBox,
    SearchDropdown,
    Stack,
    Text
} from "sprint-common-web";

import { MuxerType } from "../../../common/types/MuxerType";
import { renderEncodeSpecificationType } from "../../../common/utils/renderEncodeSpecificationType";
import { renderMuxerType } from "../../../common/utils/renderMuxerType";
import { getMuxSpecificationDashboardHref } from "../../AdminRoutes";
import { MuxSpecificationListingContext } from "../context/MuxSpecificationListingContext";
import { AddGpacMuxSpecificationButton } from "./buttons/AddGpacMuxSpecificationButton";
import { CloneMuxSpecificationButton } from "./buttons/CloneMuxSpecificationButton";
import { EditGpacMuxSpecificationButton } from "./buttons/EditGpacMuxSpecificationButton";
import { MuxDetails, MuxSpecificationListingItem } from "./MuxSpecificationListingItem";
import { useMuxSpecificationListingFilters } from "./useMuxSpecificationListingFilters";

export const MuxSpecificationListing: React.VFC = () => {
    const {
        filters,
        setSort,
        setPage,
        setPageSize,
        setName,
        clearFilters,
        typeOptions,
        setType
    } = useMuxSpecificationListingFilters();

    const { items, allItems, totalItems, isLoading, load } = useContext(MuxSpecificationListingContext);

    useEffect(() => {
        load(filters);
    }, [filters, allItems, allItems.length]);

    const columns = createListTableColumns<MuxSpecificationListingItem>([
        {
            type: "link",
            key: "name",
            name: "Name",
            minWidth: 150,
            maxWidth: 200,
            isSortable: true,
            getHref: (item) => getMuxSpecificationDashboardHref(item.id)
        },
        {
            type: "basic",
            key: "type",
            name: "Type",
            minWidth: 50,
            maxWidth: 100,
            onRender: renderMuxerType
        },
        {
            type: "basic",
            key: "encodeSpecificationType",
            name: "Encode specification type",
            minWidth: 100,
            maxWidth: 200,
            onRender: renderEncodeSpecificationType
        },
        {
            type: "basic",
            key: "details",
            name: "Details",
            minWidth: 80,
            maxWidth: 350,
            onRender: renderParameters
        },
        {
            type: "basic",
            key: "updatedDate",
            name: "Updated",
            minWidth: 100,
            maxWidth: 150,
            onRender: renderDateTime
        },
        {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 40,
            maxWidth: 40,
            buttons: [
                {
                    id: "EditGpacMuxSpecification",
                    isDisabled: isItemTypeManzanita,
                    render: EditGpacMuxSpecificationButton
                },
                {
                    id: "EditGpacMuxSpecification",
                    render: CloneMuxSpecificationButton
                }
            ]
        }
    ]);

    const addGpacMuxSpecificationButtonProps: ButtonRenderProps<ButtonProps> = {
        render: AddGpacMuxSpecificationButton
    };
    return (
        <ContentSection header="Mux Specifications" buttons={[addGpacMuxSpecificationButtonProps]}>
            <Grid>
                <GridRow>
                    <GridColumn sm={4}>
                        <SearchBox placeholder="Name" onChange={setName} value={filters.name} />
                    </GridColumn>
                    <GridColumn sm={3}>
                        <SearchDropdown
                            testId="mux-specification-filters-type"
                            placeholder={"Type"}
                            selectedKey={filters.typeId}
                            onChange={setType}
                            options={typeOptions}
                        />
                    </GridColumn>
                    <GridColumn sm={5}>
                        <Stack horizontalAlign="end">
                            <ClearFiltersButton onClear={clearFilters} isLabeled={true} />
                        </Stack>
                    </GridColumn>
                </GridRow>
                <GridRow>
                    <GridColumn sm={12}>
                        <ListTable
                            columns={columns}
                            items={items}
                            pagination={{
                                page: filters.page,
                                pageSize: filters.pageSize,
                                totalItems,
                                onPageChange: setPage,
                                onPageSizeChange: setPageSize
                            }}
                            sort={{ by: filters.sortBy, order: filters.sortOrder }}
                            onSortChange={setSort}
                            isLoading={isLoading}
                        />
                    </GridColumn>
                </GridRow>
            </Grid>
        </ContentSection>
    );
};

const renderParameters = (item?: MuxSpecificationListingItem) => {
    ensure(item);

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            {(Object.keys(item.details) as (keyof MuxDetails)[])
                .filter((property) => item.details[property] !== undefined && property !== "type")
                .map((property) => (
                    <Stack key={property} horizontal={true} tokens={{ childrenGap: 7 }}>
                        <Text variant="smallPlus">{depascalize(property)}:</Text>
                        <Text variant="smallPlus">{item.details[property]}</Text>
                    </Stack>
                ))}
        </Stack>
    );
};

const isItemTypeManzanita = (item: MuxSpecificationListingItem): boolean => {
    if (item.type === MuxerType.Manzanita) return true;
    return false;
};
